package dev.kuml.portal.web.editor

import dev.kuml.portal.web.i18n.t
import io.kvision.core.Container
import io.kvision.core.onClick
import io.kvision.html.Button
import io.kvision.html.Div
import io.kvision.html.button
import io.kvision.html.div

/** Event types the diagram stage listens for (see `EditorScreen`'s `stage.addAfterInsertHook`) and
 * that must never reach it from a toolbar interaction -- a "-" click must zoom out, not start a pan
 * drag; a wheel notch over the toolbar must scroll the page, not zoom; a double-click on "+" must
 * not additionally reset the view to fit. Stopped at the toolbar element itself rather than filtered
 * by `event.target` inside each stage handler, so the guard lives in one place and cannot drift
 * apart from the handler list.
 *
 * **R-27 addition:** `"click"` -- the stage now ALSO listens for clicks to resolve R-27's
 * click-to-select (see `EditorScreen.kt`'s `stage.addAfterInsertHook`'s new `"click"` listener and
 * `DiagramCanvas.idAt`). Without swallowing it here, a click on ANY toolbar button (zoom in/out,
 * fit, SVG/PNG download) would still bubble up to that listener afterwards, hit no
 * `[data-kuml-id]` ancestor, and clear the current selection via `DiagramSelection.escape()` --
 * every toolbar interaction would silently discard whatever the user had selected. */
private val STAGE_EVENTS_TO_SWALLOW = listOf("pointerdown", "wheel", "dblclick", "click")

/**
 * Handle returned by [Container.zoomToolbar] so `EditorScreen` can keep the toolbar's enabled state
 * in sync with the live zoom state without holding references to the individual buttons.
 */
public class ZoomToolbarHandle(
    public val element: Div,
    private val zoomInButton: Button,
    private val zoomOutButton: Button,
    private val pngButton: Button,
) {
    /** Called from `applyZoomTransform()` on every zoom/pan change -- greys out the zoom controls
     * whose click would be a no-op at the current scale (see [ZoomMath.canZoomIn]/[ZoomMath.canZoomOut]).
     * "Fit" and the download controls stay permanently enabled: "Fit" is idempotent and a greyed-out
     * fit control reads as broken, and the toolbar only exists at all while a diagram does. */
    public fun updateZoomState(
        scale: Double,
        fitScale: Double,
    ) {
        zoomInButton.disabled = !ZoomMath.canZoomIn(scale, fitScale)
        zoomOutButton.disabled = !ZoomMath.canZoomOut(scale, fitScale)
    }

    /** Disables the PNG control while a raster/encode is in flight, so an impatient double-click
     * cannot stack two exports (and two downloads) for the same diagram. */
    public fun setPngBusy(busy: Boolean) {
        pngButton.disabled = busy
    }
}

/**
 * R-7's diagram toolbar: zoom in / zoom out / fit, then (after a separator) lossless SVG download
 * and client-side PNG export. Mounted as an absolutely positioned overlay inside the stage by
 * `EditorScreen.showDiagram()`, so it exists exactly as long as a diagram does.
 *
 * The UX -- control set, order, and the separator between "view" and "export" -- is ported from
 * `obsidian-kuml`'s `KumlZoomModal.buildToolbar()` (the reference named in this wave's plan) so the
 * two kUML products behave the same; the implementation is not: there is no icon library here, so
 * the controls carry Unicode glyphs and short text labels, in the same style as this codebase's
 * other glyph buttons (`ProjectListScreen`'s "✎ Umbenennen" / "🗑 Löschen").
 *
 * Accessibility: every control carries an `aria-label` AND a `title` (the glyphs alone are not a
 * name); the bar itself is a `role="toolbar"` with its own `aria-label`; the separator is
 * `aria-hidden`. Focus rings come from `app.css`'s global `button:focus-visible` rule.
 *
 * Rendering-order convention: this container is populated ONLY through child-widget calls
 * (`button()`, `div()`) and never mixes in `content =`/`content +=` -- see the repo convention on
 * the KVision `div{}` child-ordering bug.
 */
public fun Container.zoomToolbar(
    onZoomIn: () -> Unit,
    onZoomOut: () -> Unit,
    onZoomFit: () -> Unit,
    onDownloadSvg: () -> Unit,
    onDownloadPng: () -> Unit,
): ZoomToolbarHandle {
    val bar = div(className = "kportal-zoom-toolbar")
    bar.setAttribute("role", "toolbar")
    bar.setAttribute("aria-label", t.editor.zoomToolbarAriaLabel)

    fun tool(
        glyph: String,
        label: String,
        extraClass: String,
        action: () -> Unit,
    ): Button {
        val control = bar.button(glyph, className = "kportal-zoom-tool$extraClass")
        control.setAttribute("aria-label", label)
        control.setAttribute("title", label)
        control.onClick { action() }
        return control
    }

    val zoomIn = tool("+", t.editor.zoomInLabel, "", onZoomIn)
    val zoomOut = tool("−", t.editor.zoomOutLabel, "", onZoomOut)
    tool("⤢", t.editor.zoomFitLabel, "", onZoomFit)

    val separator = bar.div(className = "kportal-zoom-toolbar-sep")
    separator.setAttribute("aria-hidden", "true")

    tool("SVG", t.editor.downloadSvgLabel, " kportal-zoom-tool-wide", onDownloadSvg)
    val png = tool("PNG", t.editor.downloadPngLabel, " kportal-zoom-tool-wide", onDownloadPng)

    bar.addAfterInsertHook {
        bar.getElement()?.let { element ->
            STAGE_EVENTS_TO_SWALLOW.forEach { type ->
                element.addEventListener(type, { event -> event.stopPropagation() })
            }
        }
    }

    return ZoomToolbarHandle(bar, zoomIn, zoomOut, png)
}
